/** Half-open interval overlap: [s1,e1) and [s2,e2) overlap iff s1<e2 && s2<e1. */
function overlaps(a, b) {
    return a.start < b.end && b.start < a.end;
}
/**
 * Detect schedule conflicts (spec §7.13) — warnings only, never blocks.
 *
 * Over non-canceled games with a scheduledAt, each occupying
 * [scheduledAt, scheduledAt + gameLengthMinutes): team overlap (same team in
 * two overlapping windows), insufficient rest (same team's consecutive games
 * with gap < minRestMinutes), and field clash (same fieldKey in overlapping
 * windows). Games without a fieldKey are skipped for field-clash checks.
 */
export function detectConflicts(games, config) {
    const lenMs = config.gameLengthMinutes * 60_000;
    const restMs = config.minRestMinutes * 60_000;
    const slots = [];
    for (const g of games) {
        if (g.status === "canceled")
            continue;
        if (!g.scheduledAt)
            continue;
        const start = Date.parse(g.scheduledAt);
        if (Number.isNaN(start))
            continue;
        const teams = [g.homeTeamId, g.awayTeamId].filter((t) => t !== undefined);
        slots.push({ game: g, start, end: start + lenMs, teams });
    }
    const warnings = [];
    // Pairwise: team overlap and field clash.
    for (let i = 0; i < slots.length; i++) {
        for (let j = i + 1; j < slots.length; j++) {
            const a = slots[i];
            const b = slots[j];
            if (!overlaps(a, b))
                continue;
            for (const t of a.teams) {
                if (b.teams.includes(t)) {
                    warnings.push({
                        type: "teamOverlap",
                        gameIds: [a.game.id, b.game.id],
                        teamId: t,
                        message: `Team is scheduled for two overlapping games (${a.game.id}, ${b.game.id}).`,
                    });
                }
            }
            const fkA = a.game.fieldKey;
            const fkB = b.game.fieldKey;
            if (fkA && fkB && fkA === fkB) {
                warnings.push({
                    type: "fieldClash",
                    gameIds: [a.game.id, b.game.id],
                    fieldKey: fkA,
                    message: `Field is double-booked for overlapping games (${a.game.id}, ${b.game.id}).`,
                });
            }
        }
    }
    // Per-team: insufficient rest between consecutive (non-overlapping) games.
    const byTeam = new Map();
    for (const s of slots) {
        for (const t of s.teams) {
            const bucket = byTeam.get(t);
            if (bucket)
                bucket.push(s);
            else
                byTeam.set(t, [s]);
        }
    }
    for (const [teamId, teamSlots] of byTeam) {
        const sorted = [...teamSlots].sort((x, y) => x.start - y.start);
        for (let k = 0; k + 1 < sorted.length; k++) {
            const earlier = sorted[k];
            const later = sorted[k + 1];
            if (overlaps(earlier, later))
                continue; // covered by teamOverlap
            const gap = later.start - earlier.end;
            if (gap >= 0 && gap < restMs) {
                warnings.push({
                    type: "insufficientRest",
                    gameIds: [earlier.game.id, later.game.id],
                    teamId,
                    message: `Only ${Math.round(gap / 60_000)} min rest between games (${earlier.game.id}, ${later.game.id}); minimum is ${config.minRestMinutes}.`,
                });
            }
        }
    }
    return warnings;
}
//# sourceMappingURL=conflicts.js.map